import React, { useState, useEffect } from 'react';
import { useSocket, persistPlayerSeat } from '../contexts/SocketContext';
import { useTranslations } from '../hooks/useTranslations';
import { translateError } from '@deadpan/shared';

export const RoomCreation: React.FC = () => {
  const { socket, isConnected, error, adoptRoom } = useSocket();
  const [playerName, setPlayerName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const t = useTranslations();

  const relevantError = localError || (error && (error.includes('create') || error.includes('generate')) && !error.includes('join') && !error.includes('already in the room') && !error.includes('duplicate') ? error : null);

  const handleCreateRoom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!socket || !isConnected || !playerName.trim()) {
      return;
    }

    setLocalError(null);
    setIsCreating(true);
    socket.emit('client:create-room', playerName.trim(), (room) => {
      setIsCreating(false);
      if (room) {
        persistPlayerSeat(room, playerName.trim());
        adoptRoom(room, playerName.trim());
      }
    });
  };

  // Listen for errors related to create-room
  useEffect(() => {
    if (error && (error.includes('create') || error.includes('generate') || error.includes('Player name is required'))) {
      setLocalError(error);
    } else {
      setLocalError(null);
    }
  }, [error]);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 max-w-md w-full border border-white/20">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
          {t.ui.createRoom}
        </h2>
        <p className="text-xs sm:text-sm text-gray-600">{t.ui.startNewGame}</p>
      </div>
      <form onSubmit={handleCreateRoom} className="space-y-5">
        <div>
          <label htmlFor="playerName" className="block text-sm font-semibold text-gray-700 mb-2">
            {t.ui.yourName}
          </label>
          <input
            id="playerName"
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder={t.ui.enterYourName}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
            required
            disabled={!isConnected || isCreating}
            maxLength={20}
          />
        </div>
        {relevantError && (
          <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-3 rounded-xl">
            {translateError(relevantError)}
          </div>
        )}
        <button
          type="submit"
          disabled={!isConnected || isCreating || !playerName.trim()}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          {isCreating ? t.ui.creating : `✨ ${t.ui.createRoom}`}
        </button>
        {!isConnected && (
          <p className="text-sm text-gray-500 text-center">{t.ui.connectingToServer}</p>
        )}
      </form>
    </div>
  );
};

